import { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import cn from 'classnames'
// Components
import Button from '@/components/newButton/Button'
import Loader from '@/components/newLoader/Loader'
import TextBody from '@/components/TextBody/TextBody'
import Title from '@/components/Title'
// Utils
import { GET_DELIVERY_OPTIONS } from '@/graphql/index'
import rootStyles from '../../pages/puppy/[id].module.scss'
import styles from './Shipment.module.scss'

const Suggestions = ({ suggestions, zipcode }) => {
  if (!suggestions?.length) return null

  if (suggestions[0]?.errorMessage) {
    return (
      <div className={cn(styles.suggestionsContainer, styles.error)}>
        <img src="/puppy/error.svg" alt="error" />
        <p className={rootStyles.description}>
          "{zipcode}" is not a valid zip or postal code. Please try again.
        </p>
      </div>
    )
  }

  if (suggestions.find((elem) => elem.slug === 'noFlight' && elem.available)) {
    return (
      <>
        <div className={styles.suggestionsContainer}>
          <div className={styles.suggestions}>
            <div className={styles.suggestion}>
              <p className={rootStyles.description}>
                <b>Please note:</b>{' '}
                {
                  suggestions.find((elem) => elem.slug === 'noFlight')
                    ?.description
                }
              </p>
            </div>
          </div>
          <div className={styles.suggestions}>
            <div className={styles.suggestion}>
              <div className={styles.iconContainer}>
                <img src="/shipping/breederPickup.svg" alt="icon" />
              </div>

              <div className={styles.content}>
                <div className={styles.type}>
                  <h4 className={styles.title}>Breeder Pickup | FREE</h4>
                </div>

                <h4 className={cn(styles.subtitle, styles.price)}>
                  Collect your new puppy directly from the breeder.
                </h4>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className={styles.suggestionsContainer}>
      <div className={styles.suggestions}>
        <div className={styles.suggestion}>
          <div className={styles.iconContainer}>
            <img src="/shipping/breederPickup.svg" alt="icon" />
          </div>

          <div className={styles.content}>
            <div className={styles.type}>
              <h4 className={styles.title}>Breeder Pickup | FREE</h4>
            </div>

            <h4 className={cn(styles.subtitle, styles.price)}>
              Collect your new puppy directly from the breeder.
            </h4>
          </div>
        </div>

        {suggestions.length > 0
          ? suggestions.map((elem, i) => {
              if (elem.available) {
                return (
                  <div key={i} className={styles.suggestion}>
                    <div className={styles.iconContainer}>
                      <img src={elem.icon} alt="icon" />
                    </div>

                    <div className={styles.content}>
                      <div className={styles.type}>
                        <h4 className={styles.title}>
                          {elem.title} | ${elem.amount / 100}
                        </h4>
                      </div>

                      <h4 className={cn(styles.subtitle, styles.price)}>
                        {elem.description}
                      </h4>
                    </div>
                  </div>
                )
              } else {
                return null
              }
            })
          : null}
      </div>
    </div>
  )
}

export default function Shipment({ dog }) {
  const [suggestions, setSuggestions] = useState(null)
  const isAvailable = dog?.isAvailable && !!dog?.breeder?.zipcode

  // Handle Input
  const ZIPCode = useFormInput('')

  function useFormInput(initialValue) {
    const [value, setValue] = useState(initialValue)
    function onChange(e) {
      if (suggestions) setSuggestions(null)
      setValue(e.target.value)
    }
    return {
      value,
      onChange,
    }
  }

  // Handle submit
  const [getShippingOptions, { loading, data }] =
    useLazyQuery(GET_DELIVERY_OPTIONS)

  const handleSubmit = async () => {
    if (!ZIPCode.value) return
    const variables = {
      dogId: dog?.id,
      zipcode: ZIPCode.value,
    }
    await getShippingOptions({ variables })
  }

  useEffect(() => {
    if (data) {
      const [
        _homeDelivery,
        _pickupNearHome,
        _borderPickup,
        _flightNanny,
        _noFlight,
      ] = data?.customerDeliveryOptions ?? []

      const _suggestions = []

      if (_homeDelivery) {
        const homeDelivery = {
          ..._homeDelivery,
          title: 'Home Delivery',
          icon: '/shipping/homeDelivery.svg',
          description: _homeDelivery.description,
          amount: _homeDelivery.amount,
          errorMessage: _homeDelivery?.errorMessage
            ? _homeDelivery.errorMessage
            : null,
          available: _homeDelivery.available,
        }

        _suggestions.push(homeDelivery)
      }
      if (_pickupNearHome) {
        const pickupNearHome = {
          ..._pickupNearHome,
          title: 'Pickup Near Home',
          icon: '/shipping/pickupNearHome.svg',
          description: _pickupNearHome.description,
          amount: _pickupNearHome.amount,
          available: _pickupNearHome.available,
        }

        _suggestions.push(pickupNearHome)
      }
      if (_borderPickup) {
        const borderPickup = {
          ..._borderPickup,
          title: 'US-Canada Border Pickup',
          icon: '/shipping/borderPickup.svg',
          description: _borderPickup.description,
          amount: _borderPickup.amount,
          available: _borderPickup.available,
        }

        _suggestions.push(borderPickup)
      }
      if (_flightNanny) {
        const flightNanny = {
          ..._flightNanny,
          title: 'Flight Nanny to Puerto Rico',
          icon: '/shipping/flightNanny.svg',
          description: _flightNanny.description,
          amount: _flightNanny.amount,
          available: _flightNanny.available,
        }

        _suggestions.push(flightNanny)
      }
      if (_noFlight) {
        const noFlight = {
          ..._noFlight,
          description: _noFlight.description,
          amount: _noFlight.amount,
          available: _noFlight.available,
        }

        _suggestions.push(noFlight)
      }

      setSuggestions(_suggestions)
    }
  }, [data])

  return (
    <>
      {isAvailable ? (
        <div className={rootStyles.section} id="pick-and-travel">
          <Title variant="h2"> Pickup & Travel </Title>
          <div className={styles.formContainer}>
            <TextBody className={styles.formTitle}>
              Find the travel cost to bring this puppy home!
            </TextBody>
            <form
              className={styles.form}
              onSubmit={(e) => {
                e.preventDefault()
                handleSubmit()
              }}
            >
              <input type="text" placeholder="Enter ZIP Code" {...ZIPCode} />
              {loading ? (
                <div className={styles.loaderContainer}>
                  <Loader theme="medium" />
                </div>
              ) : (
                <Button
                  title="Calculate"
                  onClick={handleSubmit}
                  theme={['medium', 'pink-solid']}
                />
              )}
            </form>
            <Suggestions suggestions={suggestions} zipcode={ZIPCode.value} />
          </div>
        </div>
      ) : (
        ''
      )}
    </>
  )
}
